import React, { useRef, useState, useEffect, useLayoutEffect, forwardRef } from 'react';
import styles from './EventsFilter.module.scss';
import undoButton from '../assets/undoButton.svg';
import SingleCard from './SingleCard';
import SidePanel from './SidePanel';
import useArrowGrid from '../hooks/useArrowGrid';
import { normaliseKey } from '../utils/normalizeKeys';
import { EventStatusDictionary } from '../helpers/eventHelper';
/**
 * Props
 * ──────────────────────────────────────────────────────────
 * events      : array   — list of events to display (array of event objects)
 * filterName  : string  — name of the filter (sport or league) to display
 * onBack      : () => void — function to go back to the previous view
 * currentTime : string  — current formatted time from App.js
 * onToggleSubscription : (event) => Promise<void> — function to subscribe, unsubscribe, or play the event (same logic as in App.js)
 * activeEventId - The id of the event that is currently playing (from App.js). Used to highlight the correct event as 'playing now'.
 */
const EventsFilter = forwardRef((
    {
        events = [],
        selectedSport,
        onBack,
        currentTime,
        COLS = 4,
        isSearchFilter = false,
        onExitLeft,
        disableNav = false,
        onToggleSubscription,
        activeEventId,
        openMenu,
        tz,
        handleCardSelectFromFilter,
        isLoading,
        onActivity,
        setShowPopup,
        showPopup,
        lastFocusedRef,
        setSideEventPopup
    }, ref) => {

    let filterName = selectedSport?.name || selectedSport?.alias;

    React.useImperativeHandle(ref, () => ({
        focusFirstCard() {
            setCursor({ row: 0, col: 0 });
            requestAnimationFrame(() => {
                cardRefs.current[0]?.current?.focus({ preventScroll: true });
            });
        },
    }));


    // Card references
    const cardRefs = useRef([]);
    const [isOpen, setIsOpen] = useState(false);

    // Capitalize the filter name (sport or league)
    let displayName = filterName;
    if (filterName && typeof filterName === 'object') {
        displayName = filterName.name || filterName.alias || '';
    }
    const capitalizedFilterName = displayName ? displayName.charAt(0).toUpperCase() + displayName.slice(1) : 'Live Events';

    // States for side panel
    const [sideOpen, setSideOpen] = useState(false);
    const [sideEvent, setSideEvent] = useState(null);
    const [visible, setVisible] = useState(false);
    const ctaRef = useRef(null);
    const backBtnRefFilter = useRef(null);
    const backBtnRefSidePanel = useRef(null);
    const listRef = useRef(null);
    const contentFrameRef = useRef(null);

    // (re-build refs whenever the list length changes)
    useLayoutEffect(() => {
        cardRefs.current = events.map(() => React.createRef());
        if (ref) cardRefs.current[0] && (ref.current = cardRefs.current[0].current);
    }, [events]);

    // 4-up layout
    const rowCount = Math.ceil(events.length / COLS);

    const colsInRow = (r) => {
        if (r === -1) return 1;
        if (r < 0 || r >= rowCount) return 0;
        if (r === rowCount - 1) return Math.max(1, events.length - r * COLS);
        return COLS;
    };

    const { cursor, setCursor } = useArrowGrid({
        setIsOpen,
        isOpen,
        rowStart: -1, // grid starts on row -1 (back button)
        rows: rowCount + 1,
        colsInRow,
        onExitUp: () => backBtnRefFilter.current?.focus({ preventScroll: true }),
        onExitDown: () => ctaRef.current?.focus({ preventScroll: true }),
        onExitLeft,
        enabled: !sideOpen && !disableNav && !showPopup,
        onActivity: onActivity
    });

    // When the dialog first opens, put cursor at (0,0)
    useLayoutEffect(() => {
        if (!visible) return;
        if (!isSearchFilter) {
            setCursor({ row: 0, col: 0 });
            cardRefs.current[0]?.current?.focus({ preventScroll: true });
        }
    }, [visible, setCursor, isSearchFilter]);

    useEffect(() => {
        setTimeout(() => setVisible(true), 10);
        return () => setVisible(false);
    }, []);

    // Save position of the last focused element
    const lastCursorRef = useRef({ row: 0, col: 0 });

    // Select events and send to SidePanel
    const handleSelectEvent = (event) => {
        lastCursorRef.current = cursor; // guarda la posición actual
        setSideEvent(event);
        setSideOpen(true);
    };

    // Close side panel
    const handleCloseSidePanel = () => {
        setSideOpen(false);

        const { row, col } = lastCursorRef.current ?? { row: 0, col: 0 };
        setCursor({ row, col });

        requestAnimationFrame(() => {
            const idx = row * COLS + col;
            cardRefs.current[idx]?.current?.focus({ preventScroll: true });
        });

        openMenu();
    };

    // Play now on side panel
    const handlePlayNow = async (event) => {
        if (event.shouldSignTC) {
            lastFocusedRef.current = document.activeElement;
            setSideEventPopup(event);
            setShowPopup(true);
            return;
        } else {
            if (event.status === EventStatusDictionary.LIVE){
                setSideOpen(false);
                setVisible(false);
                setTimeout(() => {
                    onBack();
                }, 350);
            }
            if (onToggleSubscription) {
                await onToggleSubscription(event);
                handleCardSelectFromFilter(selectedSport, {})
            }
        }

    };

    useEffect(() => {
        if (showPopup) return;
        const intercept = (e) => {
            if (visible) {
                e.stopPropagation();
            }
            const key = normaliseKey(e.key || e.detail?.action || e.detail?.key);
            if (sideOpen) {
                if (key === 'ArrowUp') {
                    backBtnRefSidePanel.current?.focus({ preventScroll: true });
                }
                if (key === 'ArrowDown') {
                    ctaRef.current?.focus({ preventScroll: true });
                }
            }
        };
        document.addEventListener('keydown', intercept, { capture: true });
        document.addEventListener('tvremote', intercept, { capture: true });
        return () => {
            document.removeEventListener('keydown', intercept, { capture: true });
            document.removeEventListener('tvremote', intercept, { capture: true });
        };
    }, [sideOpen, visible, showPopup]);

    const isFirstRender = useRef(true);
    useLayoutEffect(() => {
        if (showPopup) return;
        if (cursor.row === -1) {
            if (backBtnRefFilter.current) {
                backBtnRefFilter.current.focus({ preventScroll: true });
                backBtnRefFilter.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                if (backBtnRefFilter.current) {
                  backBtnRefFilter.current.classList.add('force-focus');
                  setTimeout(() => {
                    if (backBtnRefFilter.current) backBtnRefFilter.current.classList.remove('force-focus');
                  }, 200);
                }
            }
            return;
        }
        const idx = cursor.row * COLS + cursor.col;
        const cardRef = cardRefs.current[idx]?.current;
        if (cardRef && contentFrameRef.current) {

            cardRef.focus({ preventScroll: true });
            const container = contentFrameRef.current;
            const cardRect = cardRef.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            const SCROLL_MARGIN = 150;
            if (cardRect.top < containerRect.top + SCROLL_MARGIN) {
                const scrollOffset = container.scrollTop + (cardRect.top - containerRect.top) - SCROLL_MARGIN;
                container.scrollTo({ top: scrollOffset, behavior: 'smooth' });
            } else if (cardRect.bottom > containerRect.bottom - SCROLL_MARGIN) {
                const scrollOffset = container.scrollTop + (cardRect.bottom - containerRect.bottom) + SCROLL_MARGIN;
                container.scrollTo({ top: scrollOffset, behavior: 'smooth' });
            }
        }
        isFirstRender.current = false;
    }, [cursor, showPopup]);

    const handleBack = () => {
        setVisible(false);
        setTimeout(() => {
            onBack();
        }, 350);
    };
    return (
        <>
            <SidePanel
                isOpen={sideOpen}
                event={events.find(event => event.id === sideEvent?.id)}
                onClose={handleCloseSidePanel}
                onPlay={handlePlayNow}
                backBtnRef={backBtnRefSidePanel}
                ctaRef={ctaRef}
                isNowPlaying={sideEvent?.id === activeEventId}
                tz={tz}
                openMenu={openMenu}
                isLoading={isLoading}
            />
            <dialog open className={`${styles.sportsFilteredPanel} ${visible ? styles.open : ''} ${isSearchFilter ? styles.isSearchFilter : ''}`} aria-modal="true" role="dialog">
                <div className={styles.headerContainer}>
                    <div className={styles.headerTop}>
                        {!isSearchFilter && (
                            <button ref={backBtnRefFilter} className={styles.backBtn} onClick={handleBack} aria-label="Back" tabIndex={0}>
                                <img src={undoButton} alt="Back" />
                            </button>
                        )}
                        {!isSearchFilter && (
                            <div className={styles.timeDisplay}>{currentTime}</div>
                        )}
                    </div>
                    <h1 className={styles.title}>{capitalizedFilterName}</h1>
                </div>
                <div className={styles.contentFrame} ref={contentFrameRef}>
                    <div ref={listRef} className={styles.sportsList}>
                        {events.map((event, index) => (
                            <SingleCard
                                key={event.id}
                                event={event}
                                ref={(node) => {
                                    if (!cardRefs.current[index]) cardRefs.current[index] = { current: null };
                                    cardRefs.current[index].current = node;
                                    if (index === 0 && ref) {
                                        ref.current = node;
                                    }
                                }}
                                isNowPlaying={event.id === activeEventId}
                                now={currentTime}
                                onToggleSubscription={() => handleSelectEvent(event)}
                                tz={tz}
                            />
                        ))}
                    </div>
                </div>
            </dialog>
        </>
    );
});

export default EventsFilter;
